import { collection, addDoc, getDocs, query, where, doc, updateDoc, deleteDoc, Timestamp, increment, writeBatch, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const purchasesService = {
  async createPurchase(purchaseData) {
    try {
      const batch = writeBatch(db);
      
      // Get supplier details
      const supplierRef = doc(db, 'contacts', purchaseData.supplierId);
      const supplierDoc = await getDoc(supplierRef);
      const supplierName = supplierDoc.data()?.name || 'Unknown Supplier';
      
      // Get vegetable details and enrich items with vegetable names
      const enrichedItems = await Promise.all(purchaseData.items.map(async (item) => {
        const vegetableRef = doc(db, 'vegetables', item.vegetableId);
        const vegetableDoc = await getDoc(vegetableRef);
        const vegetableName = vegetableDoc.data()?.name || 'Unknown Vegetable';
        
        return {
          ...item,
          vegetableName // Add vegetable name to each item
        };
      }));
      
      // Calculate amounts
      const subtotal = enrichedItems.reduce((total, item) => {
        return total + (Number(item.quantity) * Number(item.pricePerKg));
      }, 0);

      const discount = Number(purchaseData.discount) || 0;
      const totalAmount = subtotal - discount;
      const amountPaid = Number(purchaseData.amountPaid) || 0;
      const remainingBalance = totalAmount - amountPaid;

      // Create purchase document with supplier name and enriched items
      const purchaseRef = doc(collection(db, 'purchases'));
      batch.set(purchaseRef, {
        ...purchaseData,
        items: enrichedItems, // Use enriched items with vegetable names
        supplierName,
        subtotal,
        totalAmount,
        amountPaid,
        remainingBalance,
        createdAt: new Date().toISOString(),
      });

      // Update supplier balance
      batch.update(supplierRef, {
        balance: increment(remainingBalance)
      });

      // Update inventory for each item
      for (const item of enrichedItems) {
        const vegetableRef = doc(db, 'vegetables', item.vegetableId);
        batch.update(vegetableRef, {
          stock: increment(Number(item.quantity))
        });
      }

      await batch.commit();

      return {
        id: purchaseRef.id,
        ...purchaseData,
        items: enrichedItems,
        supplierName,
        subtotal,
        totalAmount,
        amountPaid,
        remainingBalance,
      };
    } catch (error) {
      console.error('Error creating purchase:', error);
      throw error;
    }
  },

  async updateInventory(vegetableId, quantity) {
    try {
      const vegetableRef = doc(db, 'vegetables', vegetableId);
      await updateDoc(vegetableRef, {
        stock: increment(quantity)
      });
    } catch (error) {
      console.error('Error updating inventory:', error);
      throw error;
    }
  },

  async updatePurchasePayment(purchaseId, paymentData) {
    try {
      const purchaseRef = doc(db, 'purchases', purchaseId);
      const contactRef = doc(db, 'contacts', paymentData.supplierId);

      // Calculate the payment difference
      const paymentDifference = paymentData.amountPaid - (paymentData.previousAmountPaid || 0);

      // Start a batch write
      const batch = writeBatch(db);

      // Update purchase
      batch.update(purchaseRef, {
        amountPaid: paymentData.amountPaid,
        remainingBalance: paymentData.remainingBalance,
        lastUpdated: new Date().toISOString()
      });

      // Update supplier balance (decrease by payment amount)
      batch.update(contactRef, {
        balance: increment(-paymentDifference)
      });

      // Commit the batch
      await batch.commit();

      return {
        id: purchaseId,
        ...paymentData
      };
    } catch (error) {
      console.error('Error updating purchase payment:', error);
      throw error;
    }
  },

  getPurchases: async () => {
    try {
      const purchasesRef = collection(db, 'purchases');
      const querySnapshot = await getDocs(purchasesRef);
      const purchases = [];
      
      querySnapshot.forEach((doc) => {
        purchases.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log('Fetched purchases:', purchases);  // Debug log
      return purchases;
    } catch (error) {
      console.error('Error in getPurchases:', error);
      throw error;
    }
  },

  updatePurchase: async (purchaseId, updateData) => {
    try {
      const batch = writeBatch(db);
      const purchaseRef = doc(db, 'purchases', purchaseId);
      
      // Update supplier's balance in contacts collection
      if (updateData.supplierId) {
        const supplierRef = doc(db, 'contacts', updateData.supplierId);
        
        // Update supplier's balance based on the changes in remaining balance and amount paid
        batch.update(supplierRef, {
          balance: increment(updateData.remainingBalanceDifference),
          totalPaid: increment(updateData.amountPaidDifference),
          updatedAt: new Date().toISOString()
        });
      }

      // Remove fields that shouldn't be stored
      const {
        oldAmountPaid,
        oldRemainingBalance,
        amountPaidDifference,
        remainingBalanceDifference,
        ...dataToUpdate
      } = updateData;

      // Update the purchase
      batch.update(purchaseRef, {
        ...dataToUpdate,
        updatedAt: new Date().toISOString()
      });

      // Commit all updates
      await batch.commit();

      return {
        id: purchaseId,
        ...dataToUpdate
      };
    } catch (error) {
      console.error('Error updating purchase:', error);
      throw error;
    }
  },

  async getPurchasesBySupplier(supplierId) {
    try {
      const q = query(
        collection(db, 'purchases'),
        where('supplierId', '==', supplierId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting supplier purchases:', error);
      throw error;
    }
  }
};